/**
 * Single-level clip folders for LIBRARY mode.
 *
 * Folders are METADATA in the `settings` store, not a field on the clip record. That was a
 * deliberate call: `CanonicalAnimationClip` is re-projected exhaustively in three places
 * (`normalizeClipForStorage`, `libraryToJson`, `validateClip`), so an extra field would be
 * silently dropped by whichever one was not updated; a folder rename would become a write over
 * every member record; and re-saving a dirty open clip from the store's snapshot would revert it.
 *
 * Two keys hold the whole model:
 *   `folders`     — the user's folder names, in creation order. `DEFAULT_FOLDER` is never in it.
 *   `clipFolders` — clipId → folder name. A clip with no entry is in `DEFAULT_FOLDER`; that is
 *                   the ONLY spelling of "unfiled", so nothing can disagree about it.
 *
 * Settings values are not validated on write (the store's value type is `unknown`), so this
 * module sanitizes on read — the same read-time triage rule as `normalizeProjectForRead`.
 */

import { AppError, DEFAULT_FOLDER } from '../shared';
import { withDb } from './db';
import { settingsRepo } from './repos';

/** `settings` key holding the folder name list. */
export const FOLDERS_SETTINGS_KEY = 'folders';

/** `settings` key holding the clipId → folder name map. */
export const CLIP_FOLDERS_SETTINGS_KEY = 'clipFolders';

export interface FolderState {
  /** User folders in creation order; never contains `DEFAULT_FOLDER`. */
  folders: string[];
  /** clipId → folder name; a missing entry means `DEFAULT_FOLDER`. */
  clipFolders: Record<string, string>;
}

// ---------------------------------------------------------------------------
// Read-time triage
// ---------------------------------------------------------------------------

/**
 * Turn two raw `settings` values into a usable `FolderState`. Exported because
 * `commitLibraryImport` reads the same two keys from inside its own transaction and must apply
 * exactly these rules — one copy of them, not two that can drift.
 */
export function sanitizeFolderState(rawFolders: unknown, rawClipFolders: unknown): FolderState {
  const folders: string[] = [];
  const seen = new Set<string>();
  const push = (name: string): void => {
    if (name === DEFAULT_FOLDER || seen.has(name)) return;
    seen.add(name);
    folders.push(name);
  };

  if (Array.isArray(rawFolders)) {
    for (const entry of rawFolders) {
      if (typeof entry !== 'string') continue;
      const name = entry.trim();
      if (name.length > 0) push(name);
    }
  }

  const clipFolders: Record<string, string> = {};
  if (typeof rawClipFolders === 'object' && rawClipFolders !== null && !Array.isArray(rawClipFolders)) {
    const record = rawClipFolders as Record<string, unknown>;
    for (const clipId of Object.keys(record)) {
      if (clipId.length === 0) continue;
      const value = record[clipId];
      if (typeof value !== 'string') continue;
      const name = value.trim();
      // `DEFAULT_FOLDER` as a stored value is the second spelling of "unfiled"; drop it so
      // `folderOf` has one answer and a rename cannot leave the two disagreeing.
      if (name.length === 0 || name === DEFAULT_FOLDER) continue;
      clipFolders[clipId] = name;
      // A clip filed into a folder nobody listed would be unreachable in the UI. The assignment
      // is the stronger evidence, so the folder is adopted rather than the clip being unfiled.
      push(name);
    }
  }

  return { folders, clipFolders };
}

/**
 * Every mutator here is a read-modify-write over two settings keys, and `pruneFolderAssignments`
 * runs on EVERY `libraryController.refresh()` — i.e. after every clip save. Unserialized, a
 * "move to Locomotion" still in flight when Ctrl+S fires a refresh would be undone by the prune
 * writing back the map it read before the move. One module-level chain (same shape as
 * `queueProjectWrite` in projectController) makes each mutator read only after the previous one
 * has committed. Module-level, not per-caller: the keys are global, and so is the race.
 */
let folderWrites: Promise<unknown> = Promise.resolve();

function queueFolderWrite<T>(run: () => Promise<T>): Promise<T> {
  // `.then(run, run)` and not `.then(run)`: a rejected predecessor must not cancel the next
  // mutation. The caller still sees its own rejection.
  const next = folderWrites.then(run, run);
  folderWrites = next.catch(() => undefined);
  return next;
}

/**
 * Run someone else's read-modify-write of the folder keys on the same chain.
 *
 * `commitLibraryImport` writes both keys from inside its own `['clips','settings']` transaction —
 * it cannot go through the mutators here without splitting the import in two. It still has to
 * take its turn: a restore racing the prune that every `refresh()` fires would otherwise have the
 * prune write back the map it read before the import landed.
 */
export function withFolderWriteLock<T>(fn: () => Promise<T>): Promise<T> {
  return queueFolderWrite(fn);
}

/** Both keys, sanitized. Read in ONE transaction so a rename cannot be observed half-applied. */
export async function readFolderState(): Promise<FolderState> {
  return withDb(async (db) => {
    const tx = db.transaction(['settings'], 'readonly');
    const store = tx.objectStore('settings');
    const [rawFolders, rawClipFolders] = await Promise.all([
      store.get(FOLDERS_SETTINGS_KEY),
      store.get(CLIP_FOLDERS_SETTINGS_KEY),
      tx.done,
    ]);
    return sanitizeFolderState(rawFolders, rawClipFolders);
  });
}

/** The folder a clip is in. Clips with no assignment are in `DEFAULT_FOLDER`. */
export function folderOf(state: FolderState, clipId: string): string {
  const name = state.clipFolders[clipId];
  // Guarded rather than `?? DEFAULT_FOLDER`: a plain object still inherits `toString` & friends,
  // and a clip id is a string the caller supplies.
  return typeof name === 'string' && name.length > 0 ? name : DEFAULT_FOLDER;
}

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

/**
 * `rename` and `delete` touch BOTH keys. Writing them in one transaction is the whole reason
 * this module does not just call `settingsRepo.set` twice: a tab killed between the two writes
 * would otherwise leave a folder list and an assignment map that disagree.
 */
async function writeBoth(state: FolderState): Promise<FolderState> {
  await withDb(async (db) => {
    const tx = db.transaction(['settings'], 'readwrite');
    const store = tx.objectStore('settings');
    await Promise.all([
      store.put(state.folders, FOLDERS_SETTINGS_KEY),
      store.put(state.clipFolders, CLIP_FOLDERS_SETTINGS_KEY),
      tx.done,
    ]);
  });
  return state;
}

function requireName(raw: string, what: string): string {
  const name = typeof raw === 'string' ? raw.trim() : '';
  if (name.length === 0) {
    throw new AppError('FOLDER_NAME_EMPTY', `${what} needs a name.`, { name: raw });
  }
  return name;
}

function refuseDefault(name: string): void {
  if (name === DEFAULT_FOLDER) {
    throw new AppError(
      'FOLDER_RESERVED',
      `"${DEFAULT_FOLDER}" is where every unfiled animation already lives; it cannot be created, renamed or deleted.`,
      { name },
    );
  }
}

/** Create an empty folder. Names are compared exactly (case-sensitive) after trimming. */
export async function createFolder(name: string): Promise<FolderState> {
  const wanted = requireName(name, 'A folder');
  refuseDefault(wanted);

  return queueFolderWrite(async () => {
    const state = await readFolderState();
    if (state.folders.includes(wanted)) {
      throw new AppError('FOLDER_EXISTS', `There is already a folder called "${wanted}".`, {
        name: wanted,
      });
    }
    const folders = [...state.folders, wanted];
    await settingsRepo.set(FOLDERS_SETTINGS_KEY, folders);
    return { folders, clipFolders: state.clipFolders };
  });
}

/**
 * Rename a folder. Renaming ONTO an existing name merges: the members join it and `from`
 * disappears. That is the behaviour the user asked for by typing an existing name — refusing
 * would leave them to move every clip by hand for the same result.
 */
export async function renameFolder(from: string, to: string): Promise<FolderState> {
  const source = requireName(from, 'The folder being renamed');
  const target = requireName(to, 'The new folder name');
  refuseDefault(source);
  refuseDefault(target);

  return queueFolderWrite(async () => {
    const state = await readFolderState();
    if (!state.folders.includes(source)) {
      throw new AppError('FOLDER_NOT_FOUND', `There is no folder called "${source}".`, {
        name: source,
      });
    }
    if (source === target) return state;

    const merging = state.folders.includes(target);
    const folders = merging
      ? state.folders.filter((f) => f !== source)
      : // Not a merge: keep the folder where it sat in the list, so a rename does not reorder the UI.
        state.folders.map((f) => (f === source ? target : f));

    const clipFolders: Record<string, string> = {};
    for (const clipId of Object.keys(state.clipFolders)) {
      const folder = state.clipFolders[clipId];
      clipFolders[clipId] = folder === source ? target : folder;
    }

    return writeBoth({ folders, clipFolders });
  });
}

/** Delete a folder; its members fall back to `DEFAULT_FOLDER`. Clips are never deleted. */
export async function deleteFolder(name: string): Promise<FolderState> {
  const wanted = typeof name === 'string' ? name.trim() : '';
  refuseDefault(wanted);

  return queueFolderWrite(async () => {
    const state = await readFolderState();
    if (!state.folders.includes(wanted)) {
      throw new AppError('FOLDER_NOT_FOUND', `There is no folder called "${wanted}".`, {
        name: wanted,
      });
    }

    const folders = state.folders.filter((f) => f !== wanted);
    const clipFolders: Record<string, string> = {};
    for (const clipId of Object.keys(state.clipFolders)) {
      if (state.clipFolders[clipId] === wanted) continue;
      clipFolders[clipId] = state.clipFolders[clipId];
    }

    return writeBoth({ folders, clipFolders });
  });
}

/** File a clip. `DEFAULT_FOLDER` removes the assignment rather than storing the name. */
export async function moveClipToFolder(clipId: string, folder: string): Promise<FolderState> {
  const target = typeof folder === 'string' ? folder.trim() : '';

  return queueFolderWrite(async () => {
    const state = await readFolderState();

    if (target === DEFAULT_FOLDER) {
      if (!(clipId in state.clipFolders)) return state;
      const clipFolders: Record<string, string> = { ...state.clipFolders };
      delete clipFolders[clipId];
      await settingsRepo.set(CLIP_FOLDERS_SETTINGS_KEY, clipFolders);
      return { folders: state.folders, clipFolders };
    }

    if (!state.folders.includes(target)) {
      throw new AppError('FOLDER_NOT_FOUND', `There is no folder called "${target}".`, {
        name: target,
      });
    }
    if (state.clipFolders[clipId] === target) return state;

    const clipFolders: Record<string, string> = { ...state.clipFolders, [clipId]: target };
    await settingsRepo.set(CLIP_FOLDERS_SETTINGS_KEY, clipFolders);
    return { folders: state.folders, clipFolders };
  });
}

/**
 * Drop assignments for clips that no longer exist. Called from `libraryController.refresh`, i.e.
 * on every library read — hence the "nothing changed, write nothing" fast path.
 */
export async function pruneFolderAssignments(
  existingClipIds: readonly string[],
): Promise<FolderState> {
  return queueFolderWrite(async () => {
    const state = await readFolderState();
    const alive = new Set(existingClipIds);

    const clipFolders: Record<string, string> = {};
    let dropped = 0;
    for (const clipId of Object.keys(state.clipFolders)) {
      if (!alive.has(clipId)) {
        dropped++;
        continue;
      }
      clipFolders[clipId] = state.clipFolders[clipId];
    }
    if (dropped === 0) return state;

    await settingsRepo.set(CLIP_FOLDERS_SETTINGS_KEY, clipFolders);
    return { folders: state.folders, clipFolders };
  });
}
